import { isCityCommand, isHandHeld } from "../cityCommand/cityVerbs";
import { without } from "../fields";
import { type AxialCoord, hexDistance, hexEquals, hexKey, hexNeighbors } from "../hex";
import { isWorkingCityOf } from "../land/cities";
import { canTakeIn, takeHex } from "../land/territory";
import { hexAt } from "../run/board";
import { isMoveTick } from "../run/cadence";
import { seatOfUnit } from "../run/seats";
import { hostile, sameOwner, sideOf } from "../run/sides";
import { joinsByContact, leaderRoom, leadsSquad } from "../soldier/lead";
import { holdsToWork, isWorking, ordersFreely } from "../soldier/work";
import type { RunRules } from "../types/rules";
import type { FactionId, MutableHex, MutableUnit, RunState, Unit } from "../types/state";
import { isBroken } from "./morale";
import { type Ground, nextStepOf, road, walledAgainst } from "./routes";
import { unitStats } from "./unit";

/**
 * The move step: squads follow and gather, governors walk to their cities, orphans disband, and every
 * unit with a road takes its step.
 */

/**
 * A squad whose commander is gone is dissolved where it stands (and, VC, the human's men when
 * their leader falls or stands down); same array back when none is.
 */
export function disbandOrphans(units: readonly Unit[]): readonly Unit[] {
  const leaders = new Set(units.filter(leadsSquad).map((unit) => unit.id));
  if (units.every((unit) => unit.squadOf === undefined || leaders.has(unit.squadOf))) {
    return units;
  }
  return units.map((unit) => {
    if (unit.squadOf === undefined || leaders.has(unit.squadOf)) {
      return unit;
    }
    return without(unit, "squadOf");
  });
}

/**
 * Membership is remembered, not adjacency: a member far from its commander walks to it; one
 * beside it goes where the commander goes, so the squad closes on the same enemy or the same
 * hex from every side, and holds when the commander holds. A far member that stands on its
 * commander's road marches ahead of it instead of walking back into it (A38): in a lane one
 * hex wide the two would wait for each other for good.
 */
function follow(units: MutableUnit[], hexes: readonly Ground[], rules: RunRules): void {
  const roads = new Map<number, readonly AxialCoord[]>();
  const roadOf = (leader: MutableUnit): readonly AxialCoord[] => {
    let route = roads.get(leader.id);
    if (!route) {
      route = leader.destination
        ? (road(hexes, sideOf(rules, leader.owner), leader.coord, leader.destination) ?? [])
        : [];
      roads.set(leader.id, route);
    }
    return route;
  };
  for (const unit of units) {
    if (unit.squadOf === undefined) {
      continue;
    }
    const leader = units.find((candidate) => candidate.id === unit.squadOf);
    if (!leader) {
      continue;
    }
    if (hexDistance(unit.coord, leader.coord) > 1) {
      const ahead = roadOf(leader).some((coord) => hexEquals(coord, unit.coord));
      unit.destination = ahead && leader.destination ? leader.destination : leader.coord;
    } else if (leader.destination) {
      unit.destination = leader.destination;
    } else {
      delete unit.destination;
    }
  }
}

/**
 * Recruiting is showing up: a commander with room takes the own soldiers beside it that are
 * idle or were sent to its hex (the way to say "join him"), lowest id first. A seat's soldier
 * while it leads (VC) takes them the same way, up to its title's room.
 */
function gather(state: RunState, units: MutableUnit[], squadSize: number): void {
  for (const leader of units) {
    if (!leadsSquad(leader)) {
      continue;
    }
    const size = leader.commander ? squadSize : leaderRoom(state, seatOfUnit(state, leader.id));
    let room = size - units.filter((unit) => unit.squadOf === leader.id).length;
    for (const unit of units) {
      if (room <= 0) {
        break;
      }
      if (joinsByContact(state, unit, leader)) {
        unit.squadOf = leader.id;
        delete unit.destination;
        room -= 1;
      }
    }
  }
}

/**
 * A soldier sent to govern (A32) takes office when it stands in its city, or beside it while
 * an own soldier holds the city hex: the city has its mayor and the soldier is returned as
 * gone from the map. A city that is no longer the faction's working, mayorless city ends the
 * errand where the soldier stands.
 */
function seatGovernors(
  hexes: MutableHex[],
  units: MutableUnit[],
  occupant: ReadonlyMap<string, MutableUnit>,
  tick: number,
): Set<MutableUnit> {
  const seated = new Set<MutableUnit>();
  for (const unit of units) {
    if (!unit.governs) {
      continue;
    }
    const city = hexAt(hexes, unit.governs);
    if (!isWorkingCityOf(city, unit.owner, tick) || city.mayor) {
      delete unit.governs;
      delete unit.destination;
      continue;
    }
    const holder = occupant.get(hexKey(city.coord));
    const beside =
      hexDistance(unit.coord, city.coord) === 1 &&
      holder !== undefined &&
      holder.owner === unit.owner;
    if (hexEquals(unit.coord, city.coord) || beside) {
      city.mayor = true;
      // A new mayor starts from the default stance and with no banner of its own.
      delete city.stance;
      delete city.leader;
      seated.add(unit);
    }
  }
  return seated;
}

/**
 * Step 3: on the move interval every unit with a destination takes up to `move` steps along
 * its shortest road, in id order, each reading the positions the earlier ones left. A step
 * into an own unit's hex looks for a detour around every occupied hex and waits when there is
 * none, unless a city-command kingdom's soldier gives way (CC4, `givesWay`); a step into an
 * enemy unit's hex or a walled foreign hex waits (the fight step handles both). A unit that
 * captures on arrival takes every wall-free foreign hex it enters. On arrival the destination
 * clears. Before the steps every squad member turns toward its commander; after them every
 * commander with room takes the idle soldiers beside it.
 */
export function moveUnits(state: RunState): RunState {
  if (!isMoveTick(state) || state.units.length === 0) {
    return state;
  }
  const hexes: MutableHex[] = state.hexes.map((hex) => ({ ...hex }));
  const units: MutableUnit[] = state.units.map((unit) => ({ ...unit }));
  const occupant = new Map<string, MutableUnit>();
  for (const unit of units) {
    occupant.set(hexKey(unit.coord), unit);
  }
  const besideOwn = new Set(state.factions.filter((faction) => isCityCommand(state, faction)));
  // CC5: the human's soldier on its seat's order stops beside an own unit too (its flight home does not).
  const stopsBesideOwn = (unit: MutableUnit): boolean =>
    besideOwn.has(unit.owner) || (ordersFreely(state, unit) && unit.brokenTick === undefined);
  // CC4: soldiers that gave way this step; they take no step of their own after it.
  const shoved = new Set<MutableUnit>();
  /**
   * CC4, giving way: in a city-command kingdom an own soldier on a walker's road with no detour
   * trades hexes with it when it is idle (not held by the King's hand nor taking the hex it
   * stands on) or is stepping into the walker's hex itself. Measured on the shipped block, 200
   * seeds: without it a walker stood ordered on one hex for 2 minutes or more, its claim on the
   * target kept, in 67 runs against an idle King (62 of them to the clock), 24 advice runs and 32
   * mirrors; with it in none, and no idle King's run reached the clock. K2: a human's soldier
   * gives way too (it trades a hex, it takes no order): held units that never did made one idle
   * human worse for the company than an absent seat (45 against 60 wins of 200, z -1.78); giving
   * way, 71 against 60 (z +1.36), and four idle humans 6 against four absent seats' 0.
   */
  const givesWay = (blocker: MutableUnit, walker: MutableUnit): boolean => {
    if (shoved.has(blocker) || blocker.movedTick === state.tick || isBroken(state, blocker)) {
      return false;
    }
    if (blocker.destination) {
      const ahead = nextStepOf(
        hexes,
        sideOf(state.rules, blocker.owner),
        blocker.coord,
        blocker.destination,
      );
      return ahead !== undefined && hexEquals(ahead, walker.coord);
    }
    const here = hexAt(hexes, blocker.coord);
    const taking =
      here !== undefined && canTakeIn(hexes, state.rules, state.tick, here, blocker.owner);
    return !isHandHeld(state, blocker) && !taking;
  };
  follow(units, hexes, state.rules);
  // M2: under a load a soldier steps once in `carryMoveIntervals` move intervals.
  const ladenTicks = (state.rules.finds?.carryMoveIntervals ?? 1) * state.rules.moveIntervalTicks;
  // C2, many hands: a comrade beside it, read where the earlier ids have stepped to, lifts that wait.
  const manyHands = (unit: MutableUnit): boolean =>
    state.rules.company !== null &&
    hexNeighbors(unit.coord).some((coord) => {
      const other = occupant.get(hexKey(coord));
      return other !== undefined && other.owner === unit.owner && other.brokenTick === undefined;
    });
  for (const unit of units) {
    if (shoved.has(unit)) {
      continue;
    }
    if (unit.carry && state.tick - unit.movedTick < ladenTicks && !manyHands(unit)) {
      continue;
    }
    // CC5: the soldier working its road stands out the read and the take of the hex under it.
    if (unit.destination && isWorking(state, unit) && holdsToWork(state, unit)) {
      continue;
    }
    const stats = unitStats(state.rules, unit.kind);
    for (let step = 0; step < stats.move && unit.destination; step += 1) {
      if (hexEquals(unit.coord, unit.destination)) {
        break;
      }
      const side = sideOf(state.rules, unit.owner);
      let next = nextStepOf(hexes, side, unit.coord, unit.destination);
      let nextHex = next === undefined ? undefined : hexAt(hexes, next);
      if (!next || !nextHex) {
        break;
      }
      if (walledAgainst(nextHex, side)) {
        break;
      }
      const blocker = occupant.get(hexKey(next));
      // K6: an enemy in the way is fought, not walked round; an ally is walked round, never shoved.
      if (blocker && hostile(state.rules, blocker.owner, unit.owner)) {
        break;
      }
      let givingWay: MutableUnit | undefined;
      if (blocker) {
        const ahead = next;
        const aheadHex = nextHex;
        next = road(hexes, side, unit.coord, unit.destination, new Set(occupant.keys()))?.[1];
        nextHex = next === undefined ? undefined : hexAt(hexes, next);
        if (!next || !nextHex || walledAgainst(nextHex, side)) {
          if (
            !sameOwner(blocker.owner, unit.owner) ||
            !besideOwn.has(unit.owner) ||
            !givesWay(blocker, unit)
          ) {
            break;
          }
          givingWay = blocker;
          next = ahead;
          nextHex = aheadHex;
        } else if (occupant.has(hexKey(next))) {
          // The detour may end on the destination itself while an own unit holds it: wait beside it.
          // CC1, stop beside own: a city-command soldier sent there ends beside it, idle, and
          // its city's duty or the King's hand can send it on.
          if (
            stopsBesideOwn(unit) &&
            hexEquals(next, unit.destination) &&
            occupant.get(hexKey(next))?.owner === unit.owner
          ) {
            delete unit.destination;
          }
          break;
        }
      }
      const from = unit.coord;
      occupant.delete(hexKey(from));
      unit.coord = next;
      unit.movedTick = state.tick;
      occupant.set(hexKey(next), unit);
      if (givingWay) {
        // It steps back into the walker's hex and takes nothing there: it gave way, it did not march.
        givingWay.coord = from;
        givingWay.movedTick = state.tick;
        occupant.set(hexKey(from), givingWay);
        shoved.add(givingWay);
      }
      // A road never steps onto razed land, so this is the take of `canTakeIn`.
      if (
        stats.capturesOnArrival &&
        canTakeIn(hexes, state.rules, state.tick, nextHex, unit.owner)
      ) {
        takeHex(hexes, state.rules, state.tick, nextHex, unit.owner);
      }
    }
    if (unit.destination && hexEquals(unit.coord, unit.destination)) {
      delete unit.destination;
    }
  }
  const seated = seatGovernors(hexes, units, occupant, state.tick);
  const staying = seated.size === 0 ? units : units.filter((unit) => !seated.has(unit));
  if (state.rules.court) {
    gather(state, staying, state.rules.court.squadSize);
  }
  // A53: a governor leaves the map alive, so its recruit comes home; a dead soldier's never does.
  const population: Partial<Record<FactionId, number>> = { ...state.population };
  const rules = state.rules.population;
  if (rules) {
    for (const governor of seated) {
      population[governor.owner] = Math.min(
        rules.max,
        (population[governor.owner] ?? 0) + rules.unitCost,
      );
    }
  }
  return { ...state, hexes, units: staying, population };
}
